// Leitura da foto da nota com IA (0.16.0): manda a foto (ou o PDF) que a
// pessoa pôs na despesa para a função /api/nota, que devolve o valor, a data,
// os litros, o número da nota, o fornecedor e o tipo da despesa. O app só
// preenche o que ainda está em branco, e a pessoa confere antes de salvar.
// Precisa de internet.
import { supabase } from '../supabase.js';
import { textoDoErro } from './cobranca.js';
import { registrarErro } from './registro-de-erros.js';

export const TIPOS_LIDOS = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

export async function blobEmBase64(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binario = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binario);
}

export async function lerNota({ foto, empresa }, { buscar = (...a) => fetch(...a), sessaoAtual = () => supabase.auth.getSession() } = {}) {
  const { data: atual } = await sessaoAtual();
  const token = atual?.session?.access_token;
  if (!token) throw new Error('SESSAO: entre de novo no app e tente outra vez.');
  const resposta = await buscar('/api/nota', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ empresa, mime: foto.mime, base64: await blobEmBase64(foto.blob) }),
  });
  const corpo = await resposta.json().catch(() => ({}));
  if (resposta.ok && corpo.campos) return corpo.campos;
  if (resposta.status >= 500 && resposta.status !== 503) {
    registrarErro(new Error(`nota: HTTP ${resposta.status} ${corpo.erro ?? ''}`.trim()), { onde: 'nota' });
  }
  throw new Error(/^[A-Z][A-Z_]+:/.test(corpo.erro ?? '') ? corpo.erro : `NOTA: ${textoDoErro(corpo.erro) || 'não deu para ler agora. Preencha à mão.'}`);
}

// O que preencher: só os campos que o formulário tem (as chaves de `atuais`)
// e que estão em branco; a data, também se ainda é a de hoje, que o
// formulário põe sozinho; os litros, só de combustível ou Arla.
// `atuais`: { valor, data, hoje, litros, numero, fornecedor, categoria (id) };
// `categorias`: [{ id, codigo }] (as que a pessoa pode escolher).
export function oQuePreencher(campos, atuais, categorias = []) {
  const vazio = (k) => k in atuais && (atuais[k] === null || atuais[k] === undefined || String(atuais[k]).trim() === '');
  const r = {};
  if (campos.valor && vazio('valor')) r.valor = campos.valor;
  if (campos.data && 'data' in atuais && (vazio('data') || (atuais.data === atuais.hoje && campos.data !== atuais.hoje))) r.data = campos.data;
  if (campos.litros && ['diesel', 'arla'].includes(campos.categoria) && vazio('litros')) r.litros = campos.litros;
  if (campos.numero && vazio('numero')) r.numero = campos.numero;
  if (campos.fornecedor && vazio('fornecedor')) r.fornecedor = campos.fornecedor;
  if (campos.categoria && vazio('categoria')) {
    const cat = categorias.find((c) => c.codigo === campos.categoria && !c.empresa_id);
    if (cat) r.categoria = cat.id;
  }
  return r;
}

const NOMES = { valor: 'o valor', data: 'a data', litros: 'os litros', numero: 'o número da nota', fornecedor: 'o fornecedor', categoria: 'a categoria' };
export function textoDoPreenchido(preenchido) {
  const nomes = Object.keys(NOMES).filter((k) => k in preenchido).map((k) => NOMES[k]);
  if (nomes.length === 0) return 'Li a nota, mas os campos já estavam preenchidos. Confira os valores.';
  const lista = nomes.length === 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)}`;
  return `Li a nota e preenchi ${lista}. Confira antes de salvar.`;
}
